import React, {useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {goAgentService, toAgentApply} from '@/utils';
import {useShare} from '../hooks/share.hooks';
import {
  AgentInfo,
  BannerListItem,
  TodayEarningsChartItemRes,
} from './proxy.type';
import {
  getAgentInfo,
  getBannerList,
  getTodayEarningsChart,
} from './proxy.service';
import {
  AmountChange,
  HomeActiveIcon,
  HomeBettingIcon,
  HomeBetUserIcon,
  HomeCommissionIcon,
  HomeNewUserIcon,
  HomeRechargeIcon,
  ProxyCard,
  ProxyCommissionRateModal,
  ProxyCopyBlock,
  ProxyEmpty,
  ProxyH5Header,
  ProxyNavRow,
  ProxySectionTitle,
  formatProxyDate,
  getLevelImage,
  goTo,
  toPriceStr,
} from './proxy-h5.shared';
import {
  commissionDetailImage,
  customerServiceImage,
  defaultHeaderImg,
  invitationRulesImage,
  teamReportImage,
} from './proxy.variable';

type EarningsResponse = {
  rank?: string;
  exceed?: string;
  commissionAmount?: string;
  earningsChartList: TodayEarningsChartItemRes[];
};

const ProxyHome = () => {
  const {t} = useTranslation();
  const {code, refreshCode, doShare, initShare, copyCode} = useShare();
  const [agentInfo, setAgentInfo] = useState<AgentInfo>();
  const [ranking, setRanking] = useState<EarningsResponse>({
    earningsChartList: [],
  });
  const [, setBannerList] = useState<BannerListItem[]>([]);
  const [openRate, setOpenRate] = useState(false);

  useEffect(() => {
    Promise.allSettled([
      getAgentInfo(),
      getTodayEarningsChart(),
      getBannerList(),
      initShare(),
    ]).then(([agent, chart, banner]) => {
      if (agent.status === 'fulfilled') {
        if (!agent.value || Object.keys(agent.value).length === 0) {
          toAgentApply();
          return;
        }
        setAgentInfo(agent.value);
      }
      if (chart.status === 'fulfilled') {
        setRanking(chart.value || {earningsChartList: []});
      }
      if (banner.status === 'fulfilled') {
        setBannerList(banner.value || []);
      }
    });
  }, [initShare]);

  const navItems = useMemo(
    () => [
      {
        icon: teamReportImage,
        title: t('proxy.home.team-report'),
        onClick: () => goTo('ProxyTeamReport'),
      },
      {
        icon: commissionDetailImage,
        title: t('proxy.home.commission-detail'),
        onClick: () =>
          goTo('ProxyCommissionDetail', {
            userId: agentInfo?.userId,
            agentLevel: agentInfo?.agentLevel,
          }),
      },
      {
        icon: invitationRulesImage,
        title: t('proxy.home.invitation-rules'),
        onClick: () => goTo('ProxyRules'),
      },
      {
        icon: customerServiceImage,
        title: t('proxy.home.agent-line-customer-service'),
        onClick: goAgentService,
      },
    ],
    [agentInfo?.agentLevel, agentInfo?.userId, t],
  );

  const meRow = {
    rank: ranking.rank || '-',
    exceed: ranking.exceed || '0%',
    commissionAmount: ranking.commissionAmount || '0',
  };

  return (
    <div className="proxy-h5-page flex min-h-full flex-col">
      <ProxyH5Header title={t('proxy.title')} />
      <div className="flex-1 overflow-y-auto px-3 py-3">
        <div className="flex flex-col gap-3">
          <ProxyCard className="p-3">
            <div className="border-b border-[var(--proxy-line)] pb-2">
              <div className="flex items-center">
                <img
                  src={agentInfo?.userAvatar || defaultHeaderImg}
                  alt=""
                  className="h-12 w-12 rounded-full"
                />
                <div className="ml-2">
                  <div className="flex items-center">
                    <img
                      src={getLevelImage(agentInfo?.agentLevel)}
                      alt=""
                      className="h-5 w-[3.1875rem]"
                    />
                    <span className="ml-1 text-sm font-bold text-[var(--proxy-main)]">
                      {agentInfo?.userPhone || '-'}
                    </span>
                  </div>
                  <div className="mt-0.5 flex text-xs">
                    <span className="text-[var(--proxy-text)]">
                      {t('proxy.user.register-date')}
                    </span>
                    <span className="ml-1 text-[var(--proxy-text)]">
                      {formatProxyDate(agentInfo?.registerDate)}
                    </span>
                  </div>
                </div>
              </div>
            </div>
            <button
              type="button"
              className="mb-3 flex w-full items-center justify-between border-0 bg-transparent p-0 text-left"
              onClick={() => goTo('ProxyTeamReport')}>
              <div>
                <div className="mt-3 h-6 text-xs leading-6">
                  <span className="text-[var(--proxy-text)]">
                    {t('proxy.user.total-user')}
                  </span>
                  <span className="ml-1 font-bold text-[var(--proxy-second)]">
                    {agentInfo?.totalUserCount || 0}
                  </span>
                </div>
                <div className="mt-1 flex h-6 items-center text-xs leading-6">
                  <span className="text-[var(--proxy-text)]">
                    {t('proxy.user.total-commission')}
                  </span>
                  <span className="ml-1 font-bold text-[var(--proxy-second)]">
                    {toPriceStr(+(agentInfo?.totalCommissionAmount || 0))}
                  </span>
                  <button
                    type="button"
                    className="ml-2 flex h-5 w-16 items-center justify-center rounded-full bg-[var(--proxy-primary)] text-[10px] font-bold text-white"
                    onClick={event => {
                      event.stopPropagation();
                      setOpenRate(true);
                    }}>
                    <span className="mr-0.5">{t('proxy.home.rebate')}</span>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 10 10"
                      className="h-2.5 w-2.5">
                      <path
                        fill="#fff"
                        d="M7.253 4.824a.714.714 0 0 1 0 1.08l-2.48 2.148a.714.714 0 0 1-1.182-.54V3.216c0-.612.72-.94 1.182-.54z"
                      />
                    </svg>
                  </button>
                </div>
              </div>
              <svg
                viewBox="0 0 14 14"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className="h-3.5 w-3.5 shrink-0">
                <path
                  d="M5.25 10.5L8.75 7L5.25 3.5"
                  stroke="var(--proxy-muted)"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
            <div className="mb-2 bg-[#f3f5f8] px-3 py-2">
              <div className="text-xs font-bold leading-4 text-[var(--proxy-main)]">
                Today's user data
              </div>
              <div className="my-2 grid grid-cols-[1fr_1.28fr_1fr] items-start border-b border-[var(--proxy-line)] pb-2">
                <div className="min-w-0">
                  <div className="flex items-center gap-1 whitespace-nowrap">
                    <HomeActiveIcon className="h-3.5 w-3.5 shrink-0" />
                    <span className="text-xs leading-4 text-[var(--proxy-text)]">
                      {t('proxy.user.active')}
                    </span>
                  </div>
                  <div className="mt-1 text-base font-bold leading-5 text-[var(--proxy-main)]">
                    {agentInfo?.todayActiveUserCount || 0}
                  </div>
                </div>
                <div className="min-w-0 justify-self-center">
                  <div className="flex items-center gap-1 whitespace-nowrap">
                    <HomeBetUserIcon className="h-3.5 w-3.5 shrink-0" />
                    <span className="text-xs leading-4 text-[var(--proxy-text)]">
                      {t('proxy.user.recharge-user')}
                    </span>
                  </div>
                  <div className="mt-1 text-base font-bold leading-5 text-[var(--proxy-main)]">
                    {agentInfo?.todayRechargeUserCount || 0}
                  </div>
                </div>
                <button
                  type="button"
                  className="min-w-0 justify-self-end border-0 bg-transparent p-0 text-right"
                  onClick={() =>
                    goTo('ProxyNewUser', {
                      userCount: agentInfo?.todayNewUserCount || 0,
                    })
                  }>
                  <div className="flex items-center justify-end gap-1 whitespace-nowrap">
                    <HomeNewUserIcon className="h-3.5 w-3.5 shrink-0" />
                    <span className="text-xs leading-4 text-[var(--proxy-text)]">
                      {t('proxy.user.new-user')}
                    </span>
                  </div>
                  <div className="mt-1 flex items-center justify-end gap-1 text-base font-bold leading-5 text-[var(--proxy-main)]">
                    <span>{agentInfo?.todayNewUserCount || 0}</span>
                    <svg
                      viewBox="0 0 14 14"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-3.5 w-3.5">
                      <path
                        d="M5.25 10.5L8.75 7L5.25 3.5"
                        stroke="var(--proxy-muted)"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>
                </button>
              </div>
              <div className="text-xs font-bold leading-4 text-[var(--proxy-main)]">
                Total Data
              </div>
              <button
                type="button"
                className="mt-2 grid w-full grid-cols-[1fr_1.12fr_1fr] items-start border-0 bg-transparent p-0 pb-2 text-left"
                onClick={() => goTo('ProxyTeamReport')}>
                <div className="min-w-0">
                  <div className="flex items-center gap-1 whitespace-nowrap">
                    <HomeRechargeIcon className="h-3.5 w-3.5 shrink-0" />
                    <span className="text-xs leading-4 text-[var(--proxy-text)]">
                      {t('proxy.user.recharge')}
                    </span>
                  </div>
                  <div className="mt-1 text-base font-bold leading-5 text-[var(--proxy-main)]">
                    {toPriceStr(+(agentInfo?.totalRechargeAmount || 0), {
                      suffixUnit: 'K',
                    })}
                  </div>
                  <AmountChange amount={agentInfo?.todayRechargeAmount} />
                </div>
                <div className="min-w-0 justify-self-center">
                  <div className="flex items-center gap-1 whitespace-nowrap">
                    <HomeBettingIcon
                      className="h-3.5 w-3.5 shrink-0"
                      stroke="#848491"
                    />
                    <span className="text-xs leading-4 text-[var(--proxy-text)]">
                      {t('proxy.user.betting')}
                    </span>
                  </div>
                  <div className="mt-1 text-base font-bold leading-5 text-[var(--proxy-main)]">
                    {toPriceStr(+(agentInfo?.totalBetAmount || 0), {
                      suffixUnit: 'K',
                    })}
                  </div>
                  <AmountChange amount={agentInfo?.todayBetAmount} />
                </div>
                <div className="min-w-0 justify-self-end text-right">
                  <div className="flex items-center justify-end gap-1 whitespace-nowrap">
                    <HomeCommissionIcon className="h-3.5 w-3.5 shrink-0" />
                    <span className="text-xs leading-4 text-[var(--proxy-text)]">
                      {t('proxy.user.commission')}
                    </span>
                  </div>
                  <div className="mt-1 text-base font-bold leading-5 text-[var(--proxy-main)]">
                    {toPriceStr(+(agentInfo?.totalCommissionAmount || 0), {
                      suffixUnit: 'K',
                    })}
                  </div>
                  <div className="flex justify-end">
                    <AmountChange amount={agentInfo?.todayCommissionAmount} />
                  </div>
                </div>
              </button>
            </div>
          </ProxyCard>

          <ProxyCopyBlock
            code={code}
            onRefresh={refreshCode}
            onCopy={copyCode}
            onShare={() => doShare()}
            title={t('proxy.home.my-invitation-code')}
            resetLabel={t('proxy.home.reset-link')}
            shareLabel={t('proxy.home.invitation-link')}
          />

          <ProxyCard className="px-5 py-3">
            {navItems.map(item => (
              <ProxyNavRow key={item.title} {...item} />
            ))}
          </ProxyCard>

          <ProxyCard className="overflow-hidden py-3">
            <div className="px-3">
              <ProxySectionTitle title={t('proxy.home.commission-ranking')} />
            </div>
            <div className="mt-3">
              {ranking.earningsChartList.length === 0 && (
                <div className="px-3">
                  <ProxyEmpty title={t('label.noMoreData')} />
                </div>
              )}
              {ranking.earningsChartList.map((item, index) => (
                <div
                  key={`${item.userId}-${index}`}
                  className={
                    index + 1 === +ranking.rank! ? 'bg-[#00B51214]' : ''
                  }>
                  <div className="flex h-10 items-center justify-between px-3">
                    <div className="flex items-center">
                      <div className="flex h-5 w-5 items-center justify-center rounded bg-[#e5e5e5] text-xs font-bold text-[var(--proxy-main)]">
                        {index + 1}
                      </div>
                      <img
                        src={item.headImg || defaultHeaderImg}
                        alt=""
                        className="ml-3 h-6 w-6 rounded-full"
                      />
                      <span className="ml-2 text-xs text-[var(--proxy-main)]">
                        {item.phone}
                      </span>
                    </div>
                    <span className="text-base font-bold text-[var(--proxy-main)]">
                      {toPriceStr(+item.commissionAmount || 0, {fixed: 1})}
                    </span>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-1 border-t border-[var(--proxy-line)] px-3 py-3">
              <div className="flex items-center">
                <div className="flex w-8 flex-col items-center justify-center">
                  <span className="text-sm font-bold text-[var(--proxy-main)]">
                    {meRow.rank}
                  </span>
                  <span className="text-[10px] font-bold text-[var(--proxy-text)]">
                    {t('proxy.home.me')}
                  </span>
                </div>
                <img
                  src={agentInfo?.userAvatar || defaultHeaderImg}
                  alt=""
                  className="ml-3 h-7 w-7 rounded-full"
                />
                <div className="ml-2 text-[10px] text-[var(--proxy-text)]">
                  <div>{t('proxy.home.whose-commissions')}</div>
                  <div>
                    <span>{t('proxy.home.exceed')}</span>
                    <span className="ml-1 font-bold text-[#E20000]">
                      {meRow.exceed}
                    </span>
                  </div>
                </div>
                <span className="ml-auto text-base font-bold text-[var(--proxy-main)]">
                  {toPriceStr(+meRow.commissionAmount || 0, {fixed: 1})}
                </span>
              </div>
            </div>
          </ProxyCard>
        </div>
      </div>
      <ProxyCommissionRateModal
        open={openRate}
        onClose={() => setOpenRate(false)}
        level={agentInfo?.agentLevel || 1}
        onInvite={() => doShare()}
      />
    </div>
  );
};

export default ProxyHome;
